import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectLiveRevision } from '../common/slice/liveUpdates';

// A single user action can produce a burst of events -- a bulk note review, or
// an activity handover that touches the predecessor as well -- and each one
// would otherwise cost a request.
const DEFAULT_COALESCE_MS = 300;

/**
 * Re-runs `callback` whenever a live socket event touches one of `domains`.
 *
 * The container keeps ownership of its own refetch arguments, which the socket
 * middleware has no way to reconstruct. Mount is deliberately skipped: the
 * container has already loaded its own data by then, and refetching would
 * double every page load.
 *
 * @param {string[]} domains e.g. ['workkard'] or ['activity', 'note']
 * @param {Function} callback invoked with no arguments on each change
 * @param {{workkardId?: number|string, coalesceMs?: number, enabled?: boolean}} [options]
 *        `workkardId` restricts the subscription to events naming that claim,
 *        which anything rendered inside an open claim should pass. `enabled`
 *        false defers refreshes until it turns true again.
 */
export const useLiveRefresh = (domains, callback, options = {}) => {
    const { workkardId, coalesceMs = DEFAULT_COALESCE_MS, enabled = true } = options;

    const selector = useMemo(
        () => selectLiveRevision(domains, workkardId),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [domains.join(','), workkardId],
    );
    const revision = useSelector(selector);

    // Held in a ref so an inline arrow callback does not re-arm the effect on
    // every render and refetch in a loop.
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const seenRef = useRef(revision);

    // A page left open in a background tab would otherwise refetch on every event
    // in the org, forever. `document.hidden` cannot be a dependency, so a counter
    // bumped when the tab comes back to the foreground stands in for it.
    const [foregroundTick, setForegroundTick] = useState(0);
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const onVisibilityChange = () => {
            if (!document.hidden) setForegroundTick((tick) => tick + 1);
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    useEffect(() => {
        if (seenRef.current === revision) return undefined;

        // Returning without advancing seenRef is the point: the refresh is owed,
        // not dropped. Being re-enabled, or the tab coming back to the
        // foreground, pays it once -- a burst that accumulated while hidden
        // collapses into a single refetch rather than one per event.
        if (!enabled) return undefined;
        if (typeof document !== 'undefined' && document.hidden) return undefined;

        if (!coalesceMs) {
            seenRef.current = revision;
            callbackRef.current();
            return undefined;
        }
        // The debt is paid when the callback actually runs, not when the timer is
        // armed. The cleanup below cancels a pending timer, so marking it paid up
        // front meant any re-run for a reason other than `revision` -- the tab
        // returning to the foreground, `enabled` flipping -- cancelled the
        // refetch and then skipped it at the guard above, losing it for good.
        const timer = setTimeout(() => {
            seenRef.current = revision;
            callbackRef.current();
        }, coalesceMs);
        return () => clearTimeout(timer);
    }, [revision, coalesceMs, enabled, foregroundTick]);
};

export default useLiveRefresh;
